import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  ReceiptText,
  FileArchive,
  Camera,
  ArrowRight,
  Landmark,
  Award,
  Handshake,
  Briefcase,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { RESUME_DISPLAY_NAME } from "../lib/docTypeDisplayNames";
import { PROPOSAL_TEMPLATE_ROUTES } from "../lib/dashboardNavConfig";

const PICKS = [
  {
    id: "resume",
    title: `Build a ${RESUME_DISPLAY_NAME}`,
    headline: "Build resumes and cover letters that get noticed",
    desc: "ATS-optimized layouts, smart summaries, and a matching cover letter in minutes.",
    icon: FileText,
    accent: "text-blue-600",
    bg: "bg-blue-50",
    ring: "ring-blue-100",
    route: "/documents/resume/templates",
  },
  {
    id: "invoice",
    title: "Create an Invoice",
    headline: "Invoices clients trust instantly",
    desc: "Line items, totals, branded — paid faster.",
    icon: ReceiptText,
    accent: "text-orange-600",
    bg: "bg-orange-50",
    ring: "ring-orange-100",
    route: "/documents/invoice/templates",
  },
  {
    id: "pdf",
    title: "Compress a PDF",
    headline: "Shrink files up to 80% — fast and secure",
    desc: "Reduce file size while keeping documents crisp and readable.",
    icon: FileArchive,
    accent: "text-red-600",
    bg: "bg-red-50",
    ring: "ring-red-100",
    route: "/services/pdf-compression",
  },
  {
    id: "visa",
    title: "Visa / Passport Photo",
    headline: "Photos accepted without rejection",
    desc: "Country-compliant sizing and background — done in seconds.",
    icon: Camera,
    accent: "text-purple-600",
    bg: "bg-purple-50",
    ring: "ring-purple-100",
    route: "/services/visa-passport-photo",
  },
  {
    id: "business_plan",
    title: "Business Plan",
    headline: "Bank-ready plans with financials",
    desc: "Build a bank-ready plan with financials, market analysis, and executive summary.",
    icon: Landmark,
    accent: "text-emerald-600",
    bg: "bg-emerald-50",
    ring: "ring-emerald-100",
    route: PROPOSAL_TEMPLATE_ROUTES.business_plan,
  },
  {
    id: "grant_proposal",
    title: "Business Grant Proposal",
    headline: "Fundable grant proposals, faster",
    desc: "Generate a fundable business grant proposal with SWOT, risk table, and smart narratives.",
    icon: Award,
    accent: "text-violet-600",
    bg: "bg-violet-50",
    ring: "ring-violet-100",
    route: PROPOSAL_TEMPLATE_ROUTES.grant_proposal,
  },
  {
    id: "ngo_grant_proposal",
    title: "NGO Grant Proposal",
    headline: "Nonprofit grants with clear impact",
    desc: "Build a nonprofit grant proposal with objectives, budget narrative, and M&E plan.",
    icon: Handshake,
    accent: "text-amber-600",
    bg: "bg-amber-50",
    ring: "ring-amber-100",
    route: PROPOSAL_TEMPLATE_ROUTES.ngo_grant_proposal,
  },
  {
    id: "service_proposal",
    title: "Service Proposal",
    headline: "Win clients with polished scope",
    desc: "Win clients with scope, pricing, deliverables, and polished service proposals.",
    icon: Briefcase,
    accent: "text-sky-600",
    bg: "bg-sky-50",
    ring: "ring-sky-100",
    route: PROPOSAL_TEMPLATE_ROUTES.service_proposal,
  },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { api, refreshUser } = useAuth();

  const handlePick = async (pick) => {
    try {
      await api.post("/auth/onboarding/complete");
    } catch {
      /* silent — best-effort */
    }
    await refreshUser?.();
    navigate(pick.route);
  };

  const handleSkip = async () => {
    try {
      await api.post("/auth/onboarding/complete");
    } catch {
      /* silent */
    }
    await refreshUser?.();
    navigate("/dashboard");
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white text-slate-900">
      <div className="mx-auto max-w-[1100px] px-6 py-10 md:py-16">
        <div className="flex items-center gap-3 mb-10">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
            <img src="/logo.png" alt="DocMania" className="h-9 w-9 object-contain" />
          </div>
          <span className="text-2xl font-extrabold tracking-tight text-slate-900">
            <span className="text-blue-600">Doc</span>
            <span className="text-orange-500">Mania</span>
          </span>
        </div>

        <div className="text-center max-w-[760px] mx-auto" data-testid="onboarding-step-1">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-orange-600">Step 1 of 3</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight text-slate-950">
            What do you want to create?
          </h1>
          <p className="mt-4 text-lg leading-7 text-slate-600">
            Pick what you need today. You can always switch later — preview is always free.
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {PICKS.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                onClick={() => handlePick(p)}
                data-testid={`onboarding-pick-${p.id}`}
                className="group text-left"
              >
                <Card className="card-elevated transition-all duration-200 group-hover:-translate-y-0.5 group-hover:shadow-md cursor-pointer">
                  <CardContent className="py-6 flex items-start gap-4">
                    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ${p.ring}`}>
                      <Icon className={`h-6 w-6 ${p.accent}`} />
                    </div>
                    <div className="flex-1">
                      <div className="text-lg font-bold text-slate-950">{p.title}</div>
                      <div className={`text-sm font-semibold mt-0.5 ${p.accent}`}>{p.headline}</div>
                      <div className="text-sm text-slate-600 mt-2 leading-6">{p.desc}</div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-slate-700" />
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Button
            variant="link"
            onClick={handleSkip}
            data-testid="onboarding-skip-btn"
            className="text-slate-500 hover:text-slate-900"
          >
            Skip for now
          </Button>
        </div>
      </div>
    </main>
  );
}
